import { useState, useCallback } from 'react'

import { initRequestParam } from 'constants/constants'

import { useAuthContext } from 'AuthProvider'
import _ from 'lodash'
import { useApiCacheStore } from 'store/apiCacheStore'
import { normalizeDate } from 'utils/convertFromLocalDate'
import { getAPI } from 'utils/getAPI'
// import { useCurrentStatusStore } from 'store/currentStatusStore'

import type {
  GetMstSuppliersSupplierGetRequest,
  ResponseResult,
  ResponseUpdate,
  MasterSupplierUpdateMasterSupplierUpdatePostRequest,
  Supplier,
  MstSupplier,
} from 'api-client'
import type { ErrorResponse } from 'types/types'

export interface AutocompleteSupplier {
  label: string | null
  orgSupplierId: string | null
}

export interface MstSupplierSeq extends MstSupplier{
  id: string
}

export interface MstSuppliersSeq extends Supplier{
  list: MstSupplierSeq[]
}

export const useSupplier = () => {
  const [suppliers, setSuppliers] = useState<MstSupplier[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [result, setResult] = useState<ResponseResult>()
  
  const [autocompletSuppliers, setAutocompleteSuppliers] = useState<AutocompleteSupplier[]>([])

  const [supplierSearch, setSupplierSearch] = useState<Supplier>(initRequestParam)
  const [supplierSearchIsLoading, setSupplierSearchIsLoading] = useState(false)
  const [supplierSearchResult, setSupplierSearchResult] = useState<ResponseResult | undefined>(undefined)
  const [supplierUpdateResult, setSupplierUpdateResult] = useState<ResponseUpdate | undefined>(undefined)
  // const { setCurrentStatuses } = useCurrentStatusStore()
  const { callApiWithCache } = useApiCacheStore()
  const { loginUserInfo } = useAuthContext()
  const systemDate = normalizeDate(loginUserInfo?.systemDate ?? Date.now())
  if (systemDate) systemDate.setHours(0, 0, 0, 0)

  const fetchSupplierSearch = useCallback(async (searchParam: GetMstSuppliersSupplierGetRequest, forceRefresh = false) => {
    setSupplierSearch(initRequestParam)
    setSupplierSearchResult(undefined)
    setSupplierSearchIsLoading(true)
    setResult(undefined)
    setIsLoading(true)

    try {
      const response = await callApiWithCache(
        '/supplier',
        searchParam,
        async () => {
          // Actual API call
          const api = await getAPI()
          if (!api) throw new Error('API initialization failed')
          return api.getMstSuppliersSupplierGet(searchParam)
        },
        systemDate,
        forceRefresh
      )

      setSupplierSearch({
        ...response.data,
        list: _.map(response.data.list, (value) => ({
          ...value,
          id: Math.random().toString(32).substring(2),
        }))
      })

      const centersMap = response.data.list.map((value) => ({
        label: value.supplierName,
        orgSupplierId: value.orgSupplierId,
      }))
      setAutocompleteSuppliers(centersMap)
      setSupplierSearchResult(response.result)
      // setCurrentStatuses(response.result.systemInfo?.sysCurrentStatus)
    } catch (error) {
      const errorResponse = error as ErrorResponse
      setSupplierSearchResult({
        status: 'error',
        message: errorResponse.response && errorResponse.response.status === 422 ? '入力値に誤りがあります。' : `仕入先マスタの取得でエラーが発生しました。`,
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      })
    } finally {
      setSupplierSearchIsLoading(false)
      setIsLoading(false)
    }
  }, [callApiWithCache])

  const fetchSupplierUpdate = async (body: MasterSupplierUpdateMasterSupplierUpdatePostRequest): Promise<ResponseUpdate> => {
    setIsUpdating(true);
    setResult(undefined)
    try {
      const api = await getAPI()
      if (!api) throw new Error('API initialization failed')
      const response = await api.masterSupplierUpdateMasterSupplierUpdatePost(body);
      
      setResult(response.result)
      setSupplierUpdateResult(response);

      if (response.result.status !== 'success') {
        const error = new Error(response.result.message || '仕入先マスタの更新でエラーが発生しました。');
        Object.assign(error, {
            ...response
        });
        throw error;
      }

      // Clear cache for supplier after successful update
      const { clearCache } = useApiCacheStore.getState()
      clearCache('/supplier')
      
      return response;
    } catch (error) {
        if (error && typeof error === 'object' && 'result' in error) {
            const apiResponse = error as ResponseUpdate; 
            await new Promise<void>(resolve => {
              setSupplierUpdateResult(apiResponse);
              resolve();
            });
            return await Promise.reject(apiResponse);
        }
        const { response } = error as any;
        const errorResponse = {
            errors: [],
            result: {
                status: 'error',
                message: response && response.status === 422 ? '入力値に誤りがあります。' : '仕入先マスタの更新でエラーが発生しました。',
                systemDate: null,
                pageNo: 0,
                systemInfo: null,
            },
            updateCount: null,
            lastUpdateDatetime: null,
        };
        await new Promise<void>(resolve => {
          setSupplierUpdateResult(errorResponse);
            setResult(errorResponse.result)
            resolve();
        });
        return await Promise.reject(errorResponse);

    } finally {
      setIsUpdating(false);
    }
  };


  return {
  	result,
  	isLoading,
  	isUpdating,
    suppliers,
    autocompletSuppliers,

    supplierSearch,
    fetchSupplierSearch,
    supplierSearchResult,
    supplierSearchIsLoading,

    fetchSupplierUpdate,
    supplierUpdateResult,
  }
}